import React, { useMemo } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { useGetMappedStatusEvent } from '@api/emp/queryHooks';
import { useQueryDimensionData } from '@components/mapping-panel/api';
import { createFiltersGroup } from '@components/mapping-panel/utils/createFiltersUtils';
import { mappedEventDimensionType } from '@src/common/constant/enum';
import { AutoComplete as AutoCompleteStyled } from '../styled';
import { IAutoComplete } from '@src/emp-3.0/components/common/AntdEnhancement';
import { calcShortcutIntervalWithinBoundary } from '@utils/calcShortcutInterval';
import { DATE_SHORTCUTS } from '@utils/constants';
import { ESystemType } from '@src/emp-3.0/types';
import { useGetDataDictionary } from '@src/emp-3.0/api/queryHooks';
import { buildQueryPayload } from './util';

const TagValueAutoComplete: React.FC<
  { rawEventName: string; rule: any; src?: string; mappingRules?: any } & IAutoComplete
> = ({ rawEventName, rule, src, mappingRules, ...rest }) => {
  const value = rule.tag_value.value[0];
  const [from, to] = calcShortcutIntervalWithinBoundary(DATE_SHORTCUTS.last7Days, ESystemType.EMP);

  // Conditionally call useQueryDimensionData only when src is NOT 'event_subject'
  const { data: tagValueData, isLoading } = useQueryDimensionData(
    {
      fields: [mappedEventDimensionType.merged_event_tag_value],
      interval: `${from.format()}/${to.format()}`,
      filters: createFiltersGroup(rawEventName, rule.tag_key.value[0]),
      options: {
        enabled: src !== ESystemType.EventSubject,
      },
    },
    500,
  );

  const tagValuePayload = useMemo(
    () => buildQueryPayload(mappedEventDimensionType.merged_event_tag_value, rule, mappingRules),
    [rule?.tag_key?.value[0]],
  );

  // Conditionally call useGetDataDictionary only when src is 'event_subject'
  const { data: tagValueDataEventSubject, isFetching: isHostLoading } = useGetDataDictionary(
    tagValuePayload,
    mappedEventDimensionType.merged_event_tag_value,
    src === ESystemType.EventSubject,
  );

  const { data: mappedTagValueData } = useGetMappedStatusEvent('tag_value');

  // Use the appropriate data source based on src
  const finalTagValueData =
    src === ESystemType.EventSubject ? tagValueDataEventSubject : tagValueData;
  const finalIsLoading = src === ESystemType.EventSubject ? isHostLoading : isLoading;

  const tagValueOptions = useMemo(() => {
    return ((finalTagValueData as any[]) ?? []).map((item) => ({
      label: item[mappedEventDimensionType.merged_event_tag_value],
      value: item[mappedEventDimensionType.merged_event_tag_value],
    }));
  }, [finalTagValueData]);

  const Suffix = useMemo(() => {
    if (finalIsLoading) return <LoadingOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />;
    return undefined;
  }, [finalIsLoading]);

  return (
    <AutoCompleteStyled
      value={value}
      options={tagValueOptions}
      mappedData={mappedTagValueData?.data}
      suffixIcon={Suffix}
      style={{ width: '100%' }}
      {...rest}
    />
  );
};

export default TagValueAutoComplete;
